import { DtoBase } from "../Utils/DtoBase";
import { Iterable } from "../Utils/ObjUtils";
import { DeviceGroupMember } from "./DeviceGroupMember";
import { List } from "../Utils/List";
import { DeviceFilter, IncludeScope } from "./DeviceFilter";
import { TopoMap } from "../Topology/TopoMap";
import { StringUtils } from "../Utils/StringUtils";
import { DeviceGroupContainer } from "./DeviceGroupContainer";
import { Guid } from "../Utils/Guid";
import { Device } from "./Device";
import { DeviceSet } from "./DeviceSet";
import { HashSet } from "../Utils/HashSet";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";

//export interface DeviceGroupMemberWrapperJson {
//    device_group_member: DeviceGroupMemberJson;
//}

//export interface DeviceFilterWrapperJson {
//    device_filter: DeviceFilterJson;
//}

//export interface DeviceGroupJson extends DtoBaseJson {
//    name: string;
//    "type": string;
//    members: DeviceGroupMemberWrapperJson[];
//    topo_map: TopoMapJson;
//    primary_device_filter: DeviceFilterWrapperJson;
//    connected_device_filter: DeviceFilterWrapperJson;
//    show_map: string;
//    prtg_export_tags_devices: string;
//    prtg_export_tags_sensors: string;
//    parent_id: string;
//    user_id: string;
//}

export enum DeviceGroupType { Static, Dynamic, Other }

export class DeviceGroup extends DtoBase implements Iterable<DeviceGroupMember>, ObjectSerializer {
    public Name: string;
	public Type: DeviceGroupType;
	private _Members: List<DeviceGroupMember>;
	private _ChildGroups: List<DeviceGroup>;
	public PrimaryDeviceFilter: DeviceFilter;
	public ConnectedDeviceFilter: DeviceFilter;

	// Not serialized
	public OtherGroups: List<DeviceGroup>;	

	private _Map: TopoMap;
	public get Map(): TopoMap {
		if (this._Map != null)
			this._Map.Group = this;

		return this._Map;
	}
	public set Map(value: TopoMap) {
		this._Map = value;

		if (this._Map != null)
			this._Map.Group = this;
	}

	public ShowMap: boolean;

	public ShowChildGroups: boolean;

	public ShowChildDevices: boolean;

	public get HasName(): boolean {
		return !!this.Name;
    }

    public PrtgExportTags_Devices: string;
    public get HasPrtgExportTags_Devices(): boolean {
        return !StringUtils.IsNullOrWhiteSpace(this.PrtgExportTags_Devices);
    }

    public PrtgExportTags_Sensors: string;
    public get HasPrtgExportTags_Sensors(): boolean {
        return !StringUtils.IsNullOrWhiteSpace(this.PrtgExportTags_Sensors);
    }

	// Not serialized
	public ContainerParent: DeviceGroupContainer;
	public get HasContainerParent(): boolean {
		return !!this.ContainerParent;
	}

	public GroupParent: DeviceGroup;
	public get HasGroupParent(): boolean {
		return !!this.GroupParent;
	}

	public get IsEmpty(): boolean {
		if (!this._Members)
			return true;
		if (this._Members.Count <= 0)
			return true;
		return false;
	}

	public get Members(): List<DeviceGroupMember> {
		return this._Members;
	}

	public get ChildGroups(): List<DeviceGroup> {
		return this._ChildGroups;
	}

	public get HasChildGroups(): boolean {
		return !!this._ChildGroups && this._ChildGroups.Count > 0;
	}

    //public ParentId: Guid;
    //public UserId: Guid;
		
	constructor() {
		super();
		this.Name = 'New Device Group';
		this.Type = DeviceGroupType.Dynamic;
		this._Members = new List<DeviceGroupMember>();
		this._ChildGroups = new List<DeviceGroup>();
		this.PrimaryDeviceFilter = new DeviceFilter();
		this.PrimaryDeviceFilter.BaseIncludeScope = IncludeScope.AllDevices;
		this.ConnectedDeviceFilter = new DeviceFilter();
		this.ConnectedDeviceFilter.BaseIncludeScope = IncludeScope.NoDevices;
		this.Map = new TopoMap(this);
		this.ShowMap = true;
		this.ShowChildGroups = true;
		this.ShowChildDevices = false;
		this.GroupParent = null;
        this.OtherGroups = null;
        this.PrtgExportTags_Devices = "";
        this.PrtgExportTags_Sensors = "";
        //this.ParentId = Guid.Empty;
        //this.UserId = Guid.Empty;
	}

	public Clone(): DeviceGroup {
		var group = new DeviceGroup();
		group.CopyUpdateProperties(this, true);
		group.Map.Rebuild();

		return group;
	}

	public CopyUpdateProperties(after: DeviceGroup, copyMembers: boolean): void {
		this.Name = after.Name;
		this.Type = after.Type;

		if (copyMembers) {
			this._Members.Clear();
			after._Members.ForEach((member: DeviceGroupMember) => {
				var memberCopy = new DeviceGroupMember();
				ObjSerUtils.Copy(member, memberCopy);
				memberCopy.Device = member.Device;
				this._Members.Add(memberCopy);
			}, this);
		}

		this._ChildGroups.Clear();
		after._ChildGroups.ForEach((childGroup: DeviceGroup) => {
			var deviceGroupCopy = new DeviceGroup();
			ObjSerUtils.Copy(childGroup, deviceGroupCopy);
			this._ChildGroups.Add(deviceGroupCopy);
		}, this);

		ObjSerUtils.Copy(after.PrimaryDeviceFilter, this.PrimaryDeviceFilter);
		ObjSerUtils.Copy(after.ConnectedDeviceFilter, this.ConnectedDeviceFilter);

		if (copyMembers)
			ObjSerUtils.Copy(after.Map, this.Map);
		else
			ObjSerUtils.Copy(after.Map.LayoutSettings, this.Map.LayoutSettings);

		this.Map.Group = this;

		this.ShowMap = after.ShowMap;
		this.ShowChildGroups = after.ShowChildGroups;
		this.ShowChildDevices = after.ShowChildDevices;

		this.PrtgExportTags_Devices = after.PrtgExportTags_Devices;
		this.PrtgExportTags_Sensors = after.PrtgExportTags_Sensors;
	}

	public AddMember(member: DeviceGroupMember): void {
		this._Members.Add(member);
	}

	public AddMembers(devices: Iterable<Device>): void {
		if (devices == null)
			return;
		devices.ForEach((device) => {
			if (this.HasMember(device))
				return;

			this._Members.Add(new DeviceGroupMember(device));
		});
	}

	public HasMember(device: Device): boolean {
		if (!device)
			return false;

		return this._Members.Some((member) => {
			return member.HasDevice && member.Device == device;
		});
	}

	public RemoveMember(member: DeviceGroupMember): boolean {
		return this._Members.Remove(member);
	}

	public RemoveDeviceMember(memberDevice: Device): boolean {
		if (!memberDevice)
			return false;

		return this._Members.Some((member) => {
			if (!member.HasDevice)
				return;
			if (member.Device != memberDevice)
				return;

			this._Members.Remove(member);
			return true;
		});
	}

	public RemoveMembers(groupMembers: Iterable<DeviceGroupMember>): void {
		if (groupMembers == null)
			return;

		groupMembers.ForEach((member) => {
			this.RemoveMember(member);
		});
	}

	public RemoveDevicesMembers(devices: Iterable<Device>): void {
		if (!devices)
			return;

		devices.ForEach((device) => {
			this.RemoveDeviceMember(device);
		});
	}

	public Rebuild(deviceSet: DeviceSet): void {
		switch (this.Type) {
			case DeviceGroupType.Static:
				{
					console.log("STARTING STATIC GROUP REBUILD");
					let failCount = 0;
					this._Members.ForEach((member) => {
						member.Rebuild(deviceSet);
						if (!member.HasDevice) {
							++failCount;
							console.log("Resolution failure: " + member.Key.ToString());
						}
					});
					console.log(failCount + " FAILURES");
					console.log("ENDING STATIC GROUP REBUILD");
				}
				break;

			case DeviceGroupType.Dynamic:
				{
					var matchingDevices = new HashSet<Device>();
					this.PrimaryDeviceFilter.Apply(deviceSet).ForEach((primDevice) => {
						matchingDevices.Add(primDevice);

						primDevice.ConnectedDevices.ForEach((connDevice) => {
							if (this.ConnectedDeviceFilter.Match(connDevice)) {
								matchingDevices.Add(connDevice);
							}
						});
					});

					this._Members.Clear();
					matchingDevices.ForEach((device) => {
						this._Members.Add(new DeviceGroupMember(device));
					});
				}
				break;

			case DeviceGroupType.Other:
				{
					this._Members.Clear();
					if (this.OtherGroups != null) {
						this.OtherGroups.ForEach((otherGroup) => {
							otherGroup.Rebuild(deviceSet);
						});

						deviceSet.ForEach((device) => {
							if (!this.IsDeviceInOtherGroups(device)) {
								this._Members.Add(new DeviceGroupMember(device));
							}
						});
					}
				}
				break;
		}
	}

	private IsDeviceInOtherGroups(device: Device): boolean {
		return this.OtherGroups.Some((otherGroup) => {
			return otherGroup.Members.Some((member) => {
				return member.HasDevice && member.Device == device;
			});
		});
	}

	public AllDevices(): List<Device> {
		var result = new List<Device>();
		this._Members.ForEach((devGroupMember) => {
			result.Add(devGroupMember.Device);
		});

		return result;
	}

	public ForEach(callbackfn: (value: DeviceGroupMember, index: number, array: DeviceGroupMember[]) => void, thisArg?: any): void {
		this._Members.ForEach(callbackfn, thisArg);
	}

	public Some(callbackfn: (value: DeviceGroupMember, index: number, array: DeviceGroupMember[]) => boolean, thisArg?: any): boolean {
		return this._Members.Some(callbackfn, thisArg);
	}

	public Every(callbackfn: (value: DeviceGroupMember, index: number, array: DeviceGroupMember[]) => boolean, thisArg?: any): boolean {
		return this._Members.Every(callbackfn, thisArg);
    }

    //public static FromJson(json: DeviceGroupJson): DeviceGroup {
    //    var result = new DeviceGroup();

    //    result.CopyDtoBaseJson(json);

    //    if (json.name !== undefined)
    //        result.Name = json.name;

    //    if (json.type !== undefined)
    //        result.Type = DeviceGroupType[json.type];

    //    if (json.members !== undefined) {
    //        json.members.forEach((value, index, array) => {
    //            result.AddMember(DeviceGroupMember.FromJson(value.device_group_member));
    //        });
    //    }

    //    if (json.topo_map !== undefined)
    //        result._Map = TopoMap.FromJson(json.topo_map);

    //    if (json.primary_device_filter !== undefined)
    //        result.PrimaryDeviceFilter = DeviceFilter.FromJson(json.primary_device_filter.device_filter);

    //    if (json.connected_device_filter !== undefined)
    //        result.ConnectedDeviceFilter = DeviceFilter.FromJson(json.connected_device_filter.device_filter);

    //    if (json.show_map !== undefined)
    //        result.ShowMap = (json.show_map.toLowerCase() === "true");

    //    if (json.prtg_export_tags_devices !== undefined)
    //        result.PrtgExportTags_Devices = json.prtg_export_tags_devices;

    //    if (json.prtg_export_tags_sensors !== undefined)
    //        result.PrtgExportTags_Sensors = json.prtg_export_tags_sensors;

    //    if (json.parent_id !== undefined)
    //        result.ParentId = Guid.FromString(json.parent_id);

    //    if (json.user_id !== undefined)
    //        result.UserId = Guid.FromString(json.user_id);

    //    return result;
    //}

	public Write(obj: any, context: any): void {
		obj.device_group = {};
		obj = obj.device_group;

		super.Write(obj, context);

		obj.name = this.Name;
		obj.type = DeviceGroupType[this.Type];

		// Only serialize members of static groups.  The members of dynamic groups
		// are dynamically recalculated, and need not be serialized.
		if (this.Type == DeviceGroupType.Static) {
			obj.members = [];
			ObjSerUtils.WriteObjSerCollection(obj.members, this._Members, context);
		}

		if (this.HasChildGroups) {
			obj.child_groups = [];
			ObjSerUtils.WriteObjSerCollection(obj.child_groups, this._ChildGroups, context);
		}

		this.Map.Write(obj, context);

		obj.primary_device_filter = {};
		this.PrimaryDeviceFilter.Write(obj.primary_device_filter, context);

		obj.connected_device_filter = {};
		this.ConnectedDeviceFilter.Write(obj.connected_device_filter, context);

		obj.show_map = ValueEncoder.EncodeBoolean(this.ShowMap);
		obj.show_child_device_groups = ValueEncoder.EncodeBoolean(this.ShowChildGroups);
		obj.show_child_devices = ValueEncoder.EncodeBoolean(this.ShowChildDevices);

		if (this.HasPrtgExportTags_Devices)
			obj.prtg_export_tags_devices = this.PrtgExportTags_Devices

		if (this.HasPrtgExportTags_Sensors)
			obj.prtg_export_tags_sensors = this.PrtgExportTags_Sensors;
	}

	public Read(obj: any, context: any): void {
		if (!obj.device_group)
			throw new Error();
		obj = obj.device_group;

		super.Read(obj, context);

		if (obj.name !== undefined)
			this.Name = obj.name;

		if (obj.type !== undefined)
			this.Type = DeviceGroupType[<string>obj.type];

		if (obj.members !== undefined) {
			ObjSerUtils.ReadObjSerCollection(obj.members, this._Members, (value: any) => {
				return new DeviceGroupMember();
			}, context);
		}

		if (obj.child_groups !== undefined) {
			ObjSerUtils.ReadObjSerCollection(obj.child_groups, this._ChildGroups, (value: any) => {
				return new DeviceGroup();
			}, context);
		}

		if (obj.topo_map !== undefined) {
			this.Map = new TopoMap(this);
			this.Map.Read(obj, context);
			this.Map.Group = this;
		}

		if (obj.primary_device_filter !== undefined)
			this.PrimaryDeviceFilter.Read(obj.primary_device_filter, context);

		if (obj.connected_device_filter !== undefined)
			this.ConnectedDeviceFilter.Read(obj.connected_device_filter, context);

		if (obj.show_map !== undefined)
			this.ShowMap = ValueEncoder.DecodeBoolean(obj.show_map);

		if (obj.show_child_device_groups !== undefined)
			this.ShowChildGroups = ValueEncoder.DecodeBoolean(obj.show_child_device_groups);

		if (obj.show_child_devices !== undefined)
			this.ShowChildDevices = ValueEncoder.DecodeBoolean(obj.show_child_devices);

		if (obj.prtg_export_tags_devices !== undefined)
			this.PrtgExportTags_Devices = obj.prtg_export_tags_devices;

		if (obj.prtg_export_tags_sensors !== undefined)
			this.PrtgExportTags_Sensors = obj.prtg_export_tags_sensors;
	}

}
